import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { useInputId } from './hooks';

function FormInput({
  id: defaultId,
  icon: Icon,
  label,
  name,
  onChange,
  value,
  error,
  required,
  ...props
}) {
  const [id] = useInputId(defaultId);
  const handleChange = ({ target }) => {
    onChange((state) => ({
      ...state,
      [name]: target.value,
    }));
  };
  const showIcon = Icon || error;
  const isError = !!error;
  return (
    <div
      className={cn(
        'relative border rounded-md bg-white px-3 py-2 shadow-sm focus-within:ring-1',
        isError
          ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
          : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
      )}
    >
      <label htmlFor={id} className="block text-xs font-medium text-gray-900">
        <span>{label}</span>
        {!required && <span className="ml-1 font-light">(Optional)</span>}
      </label>
      <input
        id={id}
        type="text"
        name="name"
        required={required}
        className={cn(
          'block w-full border-0 p-0 text-gray-900 placeholder-gray-500 focus:ring-0 text-base',
          'focus:outline-none',
          showIcon ? 'pr-10' : ''
        )}
        onChange={handleChange}
        value={value || ''}
        {...props}
      />
      {showIcon && (
        <div
          className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
          title={error || ''}
        >
          {error ? (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          ) : (
            <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          )}
        </div>
      )}
    </div>
  );
}

FormInput.defaultProps = {
  id: '',
  icon: false,
  type: 'text',
  error: null,
  required: false,
};

FormInput.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};

export default React.memo(FormInput);
